import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
});

// Convenience helpers that unwrap `data` + normalize errors so callers can
// `await apiGet("/v3/coach/...")` instead of `const {data} = await api.get(...)`.
// Errors surface with a `.message` derived from FastAPI's `detail`.
async function _unwrap(promise) {
  try {
    const { data } = await promise;
    return data;
  } catch (e) {
    const detail = e?.response?.data?.detail;
    const msg =
      typeof detail === "string"
        ? detail
        : detail?.message || detail?.msg || e?.message || "Request failed";
    const err = new Error(msg);
    err.status = e?.response?.status;
    err.detail = detail;
    throw err;
  }
}
const _stripApi = (p) =>
  p?.startsWith?.("/api/") ? p.slice(4) : p;
export const apiGet    = (path, opts) => _unwrap(api.get(_stripApi(path), opts));
export const apiPost   = (path, body, opts) => _unwrap(api.post(_stripApi(path), body, opts));
export const apiPut    = (path, body, opts) => _unwrap(api.put(_stripApi(path), body, opts));
export const apiPatch  = (path, body, opts) => _unwrap(api.patch(_stripApi(path), body, opts));
export const apiDelete = (path, opts) => _unwrap(api.delete(_stripApi(path), opts));

// Attach Bearer token if available (belt + suspenders alongside cookies)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("af_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// VA-permission upsell — detect 403s tied to Virtual Assistant restrictions
// and surface a toast linking to the upgrade flow. Avoids the dead-end UX of
// "Forbidden" with no path forward. Throttled to once every 30 seconds so a
// burst of forbidden requests doesn't spam.
let _lastVaToastAt = 0;
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail || "";
    const detailLower = typeof detail === "string" ? detail.toLowerCase() : "";
    const isVaWall =
      status === 403 &&
      (detailLower.includes("virtual assistant") || detailLower.includes("vas cannot") ||
       detailLower.includes("va seat") || detailLower.includes("workspace owner"));
    if (isVaWall && Date.now() - _lastVaToastAt > 30_000) {
      _lastVaToastAt = Date.now();
      // Lazy-import sonner so this module stays framework-light.
      import("sonner").then(({ toast }) => {
        toast.error("This action requires a higher seat role.", {
          description: typeof detail === "string" ? detail : "Ask your team owner to upgrade your seat.",
          action: {
            label: "Show owner",
            onClick: () => {
              window.location.href = "/v3/settings#team-members";
            },
          },
          duration: 10_000,
        });
      }).catch(() => {});
    }
    return Promise.reject(error);
  },
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const fmtMoney = (n) => {
  if (n == null || n === "") return "—";
  const num = Number(n);
  if (Number.isNaN(num)) return "—";
  return num.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
};

export const fmtPct = (n) => (n == null ? "—" : `${Number(n).toFixed(1)}%`);

export const STAGE_COLOR = {
  "New Lead": "#3B82F6",
  Contacted: "#8B5CF6",
  Analyzed: "#F59E0B",
  "Hot — Buyer Interested": "#F43F5E",
  "Offer Sent": "#E76F51",
  "Under Contract": "#10B981",
  Closed: "#059669",
  Dead: "#94A3B8",
};
